import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useBlastCampaign } from "../../context";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${(props) => props.theme.colors?.background || "#FFFFFF"};
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${(props) => props.theme.colors?.border || "#E5E7EB"};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors?.text || "#111827"};
  margin: 0;
`;

const Content = styled.div`
  padding: 24px;
`;

const ResultCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: ${(props) => 
    props.$type === "success" 
      ? props.theme.colors?.successLight || "#D1FAE5"
      : props.theme.colors?.errorLight || "#FEE2E2"
  };
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${(props) => 
    props.$type === "success" 
      ? props.theme.colors?.success || "#059669"
      : props.theme.colors?.error || "#DC2626"
  };
  color: #FFFFFF;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ResultInfo = styled.div`
  flex: 1;
`;

const ResultTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors?.text || "#111827"};
  margin-bottom: 2px;
`;

const ResultCount = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => 
    props.$type === "success" 
      ? props.theme.colors?.success || "#059669"
      : props.theme.colors?.error || "#DC2626"
  };
`;

const FailedList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.backgroundSecondary || "#F9FAFB"};
`;

const FailedEmail = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.colors?.error || "#DC2626"};
  padding: 4px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.colors?.border || "#E5E7EB"};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid ${(props) => props.theme.colors?.border || "#E5E7EB"};
  background: ${(props) => props.theme.colors?.backgroundSecondary || "#F9FAFB"};
`;

const Button = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  background: ${(props) => props.theme.colors?.primary || "#3B82F6"};
  color: #FFFFFF;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.colors?.primaryDark || "#2563EB"};
  }
`;

const BlastResults = observer(({ onNavigateToContacts }) => {
  const { isSuccess, emailsSent, emailsFailed, clear } = useBlastCampaign();

  if (!isSuccess) {
    return null;
  }

  const handleNewCampaign = () => {
    onNavigateToContacts?.();
    clear();
  };

  return (
    <Overlay>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Blast Campaign Results</Title>
        </Header>

        <Content>
          <ResultCard $type="success">
            <ResultIcon $type="success">
              <CheckCircle />
            </ResultIcon>
            <ResultInfo>
              <ResultTitle>Emails Sent Successfully</ResultTitle>
              <ResultCount $type="success">{emailsSent.length}</ResultCount>
            </ResultInfo>
          </ResultCard>

          {emailsFailed.length > 0 && (
            <ResultCard $type="error">
              <ResultIcon $type="error">
                <ErrorIcon />
              </ResultIcon>
              <ResultInfo>
                <ResultTitle>Failed to Send</ResultTitle>
                <ResultCount $type="error">{emailsFailed.length}</ResultCount>
              </ResultInfo>
            </ResultCard>
          )}

          {emailsFailed.length > 0 && (
            <FailedList>
              {emailsFailed.map((email, index) => (
                <FailedEmail key={index}>{email}</FailedEmail>
              ))}
            </FailedList>
          )}
        </Content>

        <Footer>
          <Button onClick={handleNewCampaign}>Start New Campaign</Button>
        </Footer>
      </Modal>
    </Overlay>
  );
});

export default BlastResults;
